import { Mic } from 'lucide-react'

import { useTtsVoices } from '@/api/endpoints/media'
import { VoiceCarousel } from '../ViralShortsForm/VoiceCarousel'
import { toVoiceOption } from '../ViralShortsForm/voiceConstants'
// The "_shared" folder co-locates with subfactory forms because the SCSS
// classnames it uses (``advSection``, ``advSectionLabel``, ``rowIcon``,
// ``voicesLoading``) live in ViralShortsForm.module.scss — that module
// has been the de-facto shared form stylesheet since AtlasForm started
// importing it. Moving it to a top-level "form styles" module is a
// follow-up; for now we depend on it the same way every sibling does.
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'

export interface NarratorPickerProps {
  /** Drives whether one or two carousels render. */
  language: 'pt-br' | 'en' | 'both'
  /** Currently selected PT voice id (empty string = no override). */
  voicePt: string
  /** Currently selected EN voice id (empty string = no override). */
  voiceEn: string
  onVoicePtChange: (value: string) => void
  onVoiceEnChange: (value: string) => void
}

/**
 * Shared narrator picker used by every factory form's "advanced" panel.
 * Encapsulates the three pieces that used to be duplicated across 8
 * forms:
 *
 * 1. ``useTtsVoices('', 'all')`` — aggregate Edge + Kokoro + Piper
 *    voices into one catalogue; routing at synthesis time stays
 *    server-side.
 * 2. Filter by language and adapt the section count: ``language='both'``
 *    renders two side-by-side carousels (PT + EN), single-language
 *    renders one.
 * 3. Loading / empty fallbacks so a slow ``/tts-voices`` call or a
 *    misconfigured provider doesn't render a blank advanced panel.
 *
 * Each form just wires the form-state values + setters; nothing else.
 */
export function NarratorPicker({
  language,
  voicePt,
  voiceEn,
  onVoicePtChange,
  onVoiceEnChange,
}: NarratorPickerProps) {
  const { data: voicesData, isPending } = useTtsVoices('', 'all')
  const ptVoices = (voicesData?.voices ?? [])
    .filter((v) => v.language === 'pt-br')
    .map(toVoiceOption)
  const enVoices = (voicesData?.voices ?? [])
    .filter((v) => v.language === 'en')
    .map(toVoiceOption)
  const provider = voicesData?.provider ?? ''

  if (language === 'both') {
    return (
      <>
        <Section label="Narrador · PT">
          <Carousel
            pending={isPending}
            options={ptVoices}
            provider={provider}
            value={voicePt}
            onChange={onVoicePtChange}
          />
        </Section>
        <Section label="Narrador · EN">
          <Carousel
            pending={isPending}
            options={enVoices}
            provider={provider}
            value={voiceEn}
            onChange={onVoiceEnChange}
          />
        </Section>
      </>
    )
  }

  const isEn = language === 'en'
  return (
    <Section label="Narrador">
      <Carousel
        pending={isPending}
        options={isEn ? enVoices : ptVoices}
        provider={provider}
        value={isEn ? voiceEn : voicePt}
        onChange={isEn ? onVoiceEnChange : onVoicePtChange}
      />
    </Section>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          <Mic />
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}

interface CarouselProps {
  pending: boolean
  options: ReturnType<typeof toVoiceOption>[]
  provider: string
  value: string
  onChange: (v: string) => void
}

function Carousel({ pending, options, provider, value, onChange }: CarouselProps) {
  if (pending) {
    return <span className={styles.voicesLoading}>Carregando vozes…</span>
  }
  if (options.length === 0) {
    return (
      <span className={styles.voicesLoading}>
        Nenhuma voz disponível para o provider “{provider || 'desconhecido'}”.
      </span>
    )
  }
  return <VoiceCarousel options={options} value={value} onValueChange={onChange} />
}
